const productMain = document.querySelector('main[data-template*="product"]');

if (productMain) {
  const imports = new Map();
  const importOnce = (path) => {
    if (!imports.has(path)) {
      const request = import(path).catch((error) => {
        imports.delete(path);
        throw error;
      });
      imports.set(path, request);
    }
    return imports.get(path);
  };

  const warnDeferredFailure = (feature, error) => {
    console.warn(`[Velzeen ${feature}] Deferred module failed to load.`, error);
  };

  const supportsAbortableListeners = (() => {
    if (typeof window.AbortController !== 'function') return false;

    try {
      const controller = new window.AbortController();
      const noop = () => {};
      let signalRead = false;
      const options = Object.defineProperty({}, 'signal', {
        get() {
          signalRead = true;
          return controller.signal;
        },
      });

      window.addEventListener('vz-abort-signal-test', noop, options);
      window.removeEventListener('vz-abort-signal-test', noop, options);
      controller.abort();
      return signalRead;
    } catch {
      return false;
    }
  })();

  const createAbortController = () => {
    if (!supportsAbortableListeners) return null;

    try {
      return new window.AbortController();
    } catch {
      return null;
    }
  };

  const withAbortSignal = (options, controller) => {
    if (!controller?.signal) return options;
    return { ...options, signal: controller.signal };
  };

  const abortIfSupported = (controller) => {
    if (!controller || typeof controller.abort !== 'function') return;
    controller.abort();
  };

  let deliveryReady = false;
  let deliveryPromise;
  const deliveryController = createAbortController();

  function showDeliveryLoaderError(source) {
    const card = source?.closest?.('.vz-delivery-card');
    const message = card?.querySelector('[data-vz-pincode-message]');
    if (!message) return;

    card.classList.add('vz-delivery-card--error');
    card.classList.remove('vz-delivery-card--ready');
    message.textContent = 'Unable to load the delivery checker right now. Please try again.';
  }

  function loadDelivery(source) {
    if (deliveryReady) return Promise.resolve(true);
    if (deliveryPromise) return deliveryPromise;

    deliveryPromise = importOnce('./velzeen-delhivery-eta.js')
      .then(() => {
        deliveryReady = true;
        abortIfSupported(deliveryController);
        return true;
      })
      .catch((error) => {
        deliveryPromise = undefined;
        warnDeferredFailure('delivery', error);
        showDeliveryLoaderError(source);
        return false;
      });

    return deliveryPromise;
  }

  document.addEventListener(
    'focusin',
    (event) => {
      if (event.target instanceof Element && event.target.matches('[data-vz-pincode]')) {
        void loadDelivery(event.target);
      }
    },
    withAbortSignal({ capture: true }, deliveryController)
  );

  document.addEventListener(
    'click',
    (event) => {
      if (deliveryReady || !(event.target instanceof Element)) return;
      const button = event.target.closest('[data-vz-pincode-check]');
      if (!(button instanceof HTMLButtonElement)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      void loadDelivery(button).then((loaded) => {
        if (loaded) button.click();
      });
    },
    withAbortSignal({ capture: true }, deliveryController)
  );

  const reviewInteractionSelector = [
    '[data-vz-judgeme-launcher]',
    '[data-vz-judgeme-open]',
    '.jdgm-review-widget',
    '.jdgm-widget',
    '[id*="judge_me"]',
    '[id*="judgeme"]',
    '[class*="judge-me"]',
    '[class*="judgeme"]',
    'a[href="#product-reviews"]',
    'a[href="#judgeme_product_reviews"]',
    'a[href*="#judgeme"]',
  ].join(',');

  const reviewReplaySelector = [
    '[data-vz-judgeme-open]',
    'a[href="#product-reviews"]',
    'a[href="#judgeme_product_reviews"]',
    'a[href*="#judgeme"]',
  ].join(',');

  let reviewsReady = false;
  let reviewsPromise;
  let reviewViewportObserver;
  const reviewController = createAbortController();

  function findReviewSurface() {
    return productMain.querySelector([
      '.jdgm-review-widget',
      '#judgeme_product_reviews',
      '[id*="judgeme_product_reviews"]',
      '[id*="judge_me_product_reviews"]',
    ].join(','));
  }

  function fallbackToReviews() {
    const surface = findReviewSurface();
    if (surface instanceof HTMLElement) {
      surface.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  function loadReviews({ fromViewport = false } = {}) {
    if (reviewsReady) return Promise.resolve(true);
    if (reviewsPromise) return reviewsPromise;
    if (fromViewport) reviewViewportObserver?.disconnect();

    productMain.dataset.vzReviewsLoad = 'loading';
    reviewsPromise = importOnce('./velzeen-judgeme-drawer.js')
      .then(() => {
        reviewsReady = true;
        productMain.dataset.vzReviewsLoad = 'ready';
        reviewViewportObserver?.disconnect();
        abortIfSupported(reviewController);
        return true;
      })
      .catch((error) => {
        reviewsPromise = undefined;
        productMain.dataset.vzReviewsLoad = 'failed';
        warnDeferredFailure('reviews', error);
        return false;
      });

    return reviewsPromise;
  }

  document.addEventListener(
    'focusin',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest(reviewInteractionSelector)) void loadReviews();
    },
    withAbortSignal({ capture: true }, reviewController)
  );

  document.addEventListener(
    'pointerdown',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest(reviewInteractionSelector)) void loadReviews();
    },
    withAbortSignal({ capture: true, passive: true }, reviewController)
  );

  document.addEventListener(
    'click',
    (event) => {
      if (reviewsReady || !(event.target instanceof Element)) return;

      const control = event.target.closest(reviewReplaySelector);
      const isMobileReviewLink =
        control instanceof HTMLAnchorElement && window.matchMedia('(max-width: 749px)').matches;
      const shouldReplay = control?.matches('[data-vz-judgeme-open]') || isMobileReviewLink;

      if (!shouldReplay) {
        if (event.target.closest(reviewInteractionSelector)) void loadReviews();
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      void loadReviews().then((loaded) => {
        if (loaded && control instanceof HTMLElement) control.click();
        else fallbackToReviews();
      });
    },
    withAbortSignal({ capture: true }, reviewController)
  );

  function observeReviewProximity() {
    if (!('IntersectionObserver' in window) || reviewsReady) return;

    const launcher = productMain.querySelector('[data-vz-judgeme-launcher]');
    const surface = findReviewSurface();
    const targets = [launcher, surface].filter((target, index, items) =>
      target instanceof Element && items.indexOf(target) === index
    );
    if (!targets.length) return;

    reviewViewportObserver?.disconnect();
    reviewViewportObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          void loadReviews({ fromViewport: true });
        }
      },
      { rootMargin: '700px 0px', threshold: 0 }
    );

    targets.forEach((target) => reviewViewportObserver.observe(target));
  }

  observeReviewProximity();

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element && productMain.contains(event.target)) {
      observeReviewProximity();
    }
  });

  function updateBenefitRail(rail, index) {
    const slides = [...rail.querySelectorAll('[data-vz-benefit-slide]')];
    if (!slides.length) return;

    const safeIndex = ((index % slides.length) + slides.length) % slides.length;
    const card = rail.closest('.vz-benefit-card');
    const dots = [...(card?.querySelectorAll('.vz-benefit-dot') || [])];
    const count = card?.querySelector('[data-vz-benefit-count]');

    dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === safeIndex));
    if (count) count.textContent = `${String(safeIndex + 1).padStart(2, '0')} / ${String(slides.length).padStart(2, '0')}`;
    rail.dataset.vzActiveBenefit = String(safeIndex);
  }

  function initializeBenefitRail(rail) {
    if (!(rail instanceof HTMLElement) || rail.dataset.vzCarouselBound === 'true') return;
    const slides = [...rail.querySelectorAll('[data-vz-benefit-slide]')];
    if (!slides.length) return;

    rail.dataset.vzCarouselBound = 'true';
    const card = rail.closest('.vz-benefit-card');
    let timer;
    let resumeTimer;
    let scrollFrame;

    const goTo = (index, smooth = true) => {
      const safeIndex = ((index % slides.length) + slides.length) % slides.length;
      rail.scrollTo({ left: slides[safeIndex].offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
      updateBenefitRail(rail, safeIndex);
    };

    const stop = () => window.clearInterval(timer);
    const start = () => {
      stop();
      if (slides.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      timer = window.setInterval(() => {
        const current = Number.parseInt(rail.dataset.vzActiveBenefit || '0', 10) || 0;
        goTo(current + 1);
      }, 2000);
    };

    const pauseThenResume = () => {
      stop();
      window.clearTimeout(resumeTimer);
      resumeTimer = window.setTimeout(start, 4500);
    };

    rail.addEventListener('scroll', () => {
      window.cancelAnimationFrame(scrollFrame);
      scrollFrame = window.requestAnimationFrame(() => {
        const nearest = slides.reduce((best, slide, index) => {
          const distance = Math.abs(rail.scrollLeft - slide.offsetLeft);
          return distance < best.distance ? { index, distance } : best;
        }, { index: 0, distance: Number.POSITIVE_INFINITY });
        updateBenefitRail(rail, nearest.index);
      });
    }, { passive: true });

    rail.addEventListener('pointerdown', pauseThenResume, { passive: true });
    rail.addEventListener('focusin', pauseThenResume);
    card?.addEventListener('mouseenter', stop);
    card?.addEventListener('mouseleave', start);
    document.addEventListener('visibilitychange', () => document.hidden ? stop() : start());

    updateBenefitRail(rail, 0);
    start();
  }

  function initializeGalleryEdgeLoop(slideshow) {
    if (!(slideshow instanceof HTMLElement) || slideshow.dataset.vzEdgeLoop === 'true') return;

    const scroller = slideshow.querySelector('[ref="scroller"]') || slideshow;
    let startX = 0;
    let startY = 0;

    scroller.addEventListener('touchstart', (event) => {
      const touch = event.touches[0];
      startX = touch?.clientX || 0;
      startY = touch?.clientY || 0;
    }, { passive: true });

    scroller.addEventListener('touchend', (event) => {
      const touch = event.changedTouches[0];
      if (!touch) return;

      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;
      if (Math.abs(dx) < 48 || Math.abs(dx) <= Math.abs(dy)) return;

      window.setTimeout(() => {
        if (dx < 0 && slideshow.atEnd && typeof slideshow.next === 'function') slideshow.next();
        if (dx > 0 && slideshow.atStart && typeof slideshow.previous === 'function') slideshow.previous();
      }, 60);
    }, { passive: true });

    slideshow.dataset.vzEdgeLoop = 'true';
  }

  function initializeProductSection(scope = productMain) {
    const root = scope instanceof Element ? scope : productMain;
    if (root.matches?.('[data-vz-benefit-rail]')) initializeBenefitRail(root);
    root.querySelectorAll?.('[data-vz-benefit-rail]').forEach(initializeBenefitRail);

    if (root.matches?.('slideshow-component[infinite]')) initializeGalleryEdgeLoop(root);
    root.querySelectorAll?.('slideshow-component[infinite]').forEach(initializeGalleryEdgeLoop);
  }

  initializeProductSection();

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element && productMain.contains(event.target)) {
      initializeProductSection(event.target);
    }
  });

  productMain.addEventListener('toggle', (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open || !details.closest('.product-details')) return;

    const group = details.closest('.product-details');
    group?.querySelectorAll('details[open]').forEach((other) => {
      if (other !== details) other.removeAttribute('open');
    });
  }, true);

  productMain.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-vz-size-open]')) {
      event.preventDefault();
      const dialog = document.getElementById('VelzeenSizeGuide');
      if (dialog instanceof HTMLDialogElement && !dialog.open) dialog.showModal();
      return;
    }

    const close = target.closest('[data-vz-size-close]');
    if (close) {
      const dialog = close.closest('dialog');
      if (dialog instanceof HTMLDialogElement) dialog.close();
      return;
    }

    if (target.closest('[data-vz-scroll-section="care"]')) {
      const rows = [...productMain.querySelectorAll('.product-details details')];
      const care = rows.find((row) => /care|fabric/i.test(row.querySelector('summary')?.textContent || '')) || rows[1];
      if (care) {
        care.open = true;
        care.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  });

  const sizeDialog = document.getElementById('VelzeenSizeGuide');
  sizeDialog?.addEventListener('click', (event) => {
    if (event.target === sizeDialog) sizeDialog.close();
  });

  let recentlyViewedReady = false;
  async function addRecentlyViewed() {
    if (recentlyViewedReady) return;
    recentlyViewedReady = true;

    const productId =
      productMain.querySelector('variant-picker[data-product-id]')?.dataset.productId ||
      productMain.querySelector('[data-pdp-purchase][data-product-id]')?.dataset.productId;

    if (!productId) return;
    const { RecentlyViewed } = await importOnce('./recently-viewed-products.js');
    RecentlyViewed.addProduct(productId);
  }

  const scheduleRecentlyViewed = () => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => void addRecentlyViewed(), { timeout: 5000 });
    } else {
      window.setTimeout(() => void addRecentlyViewed(), 3500);
    }
  };

  if (document.readyState === 'complete') scheduleRecentlyViewed();
  else window.addEventListener('load', scheduleRecentlyViewed, { once: true });
}
